// Prisoverslag (A4) for en bestilling i klubbinfo, til å skrive ut eller lagre som PDF og legge ved
// e-posten til klubben (vinnsel, 04.11). Viser klubben, hvem i klubben det er til, varene med pris og
// rabatt (også trykk og annet arbeid), og sum inkl. mva. Ikke et pristilbud: det sendes fra ordren etter «Gjør om til ordre».
// Kostpris og dekningsbidrag skrives aldri ut, og heller ikke teksten på bestillingen (den er intern).
// Ansatte står bare med fornavn (S8). Alt fra brukere og databasen escapes (#30).
import { escapeHtml } from '../../utils/format';
import { linjeTall, oppsummering, kr } from '../../tilbud/kalkyle';
import { butikkFor } from '../../tilbud/butikker';
import { varekodeTekst } from '../../tilbud/varekode';
import { rolleNavn, sarIdrettNavn } from './klubbApi';

const e = (v) => escapeHtml(v);
const fornavn = (n) => String(n || '').trim().split(/\s+/)[0] || '';
const tall = (v) => Number(String(v ?? '').replace(',', '.')) || 0;

export const butikkidForKlubb = (klubb) => (klubb?.butikk === 'SLEMMESTAD' ? 2 : 1);

// person: kontaktpersonen i klubben prisoverslaget er til (eller null = bare klubben).
// ansatt: den som skriver ut (fullt navn; bare fornavnet skrives ut).
export function prisoverslagHtml({ klubb, innlegg, person = null, ansatt = '', utskrevet = new Date() }) {
  const linjer = innlegg?.prislinjer || [];
  const butikk = butikkFor(butikkidForKlubb(klubb));
  const sum = oppsummering(linjer);
  const harRabatt = linjer.some((l) => linjeTall(l).rabattKr > 0);
  const sar = sarIdrettNavn(klubb, innlegg?.sarIdrettId);
  const klubbNavn = `${klubb?.navn || ''}${klubb?.kortnavn ? ` (${klubb.kortnavn})` : ''}`;
  // Klubbansvarlig hos oss, ellers den som skriver ut.
  const ansvarlige = (klubb?.klubbansvarlige || []).map(fornavn).filter(Boolean);
  const kontaktHosOss = ansvarlige.length ? ansvarlige.join(', ') : fornavn(ansatt);
  const mva = sum.total - sum.total / 1.25;
  const dato = utskrevet.toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });

  const rader = linjer.map((l) => {
    const t = linjeTall(l);
    const antall = Math.max(1, Math.round(tall(l.antall) || 1));
    return `<tr>
      <td><strong>${e(l.artikkel || 'Vare')}</strong>${l.type === 'arbeid' ? '<div class="muted">Trykk / arbeid</div>' : varekodeTekst(l.ean) ? `<div class="muted">${e(varekodeTekst(l.ean))}</div>` : ''}</td>
      <td class="h">${e(antall)}</td>
      <td class="h">${e(kr(tall(l.pris)))}</td>
      ${harRabatt ? `<td class="h">${t.rabattKr > 0 ? e(kr(-t.rabattKr)) : ''}</td>` : ''}
      <td class="h"><strong>${e(kr(t.total))}</strong></td>
    </tr>`;
  }).join('');

  const til = person
    ? `${e(person.navn)}<br><span class="muted">${e([rolleNavn(person.rolle), sarIdrettNavn(klubb, person.sarIdrettId), person.tittel].filter(Boolean).join(' · '))}</span>${person.telefon ? `<br>${e(person.telefon)}` : ''}${person.epost ? `<br>${e(person.epost)}` : ''}`
    : '';

  return `<!doctype html>
<html lang="no">
<head>
<meta charset="utf-8">
<title>Prisoverslag ${e(klubb?.kortnavn || klubb?.navn || '')} – ${e(innlegg?.tittel || '')}</title>
<style>
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11pt; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12pt; border-bottom: 2px solid #111; padding-bottom: 8pt; margin-bottom: 12pt; }
  h1 { font-size: 22pt; margin: 0; }
  .selger { margin-top: 3pt; color: #333; font-size: 9.5pt; }
  .datoboks { text-align: right; font-size: 10pt; }
  .muted { color: #555; font-size: 9pt; }
  .info { display: flex; gap: 18pt; margin-bottom: 12pt; }
  .info > div { flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .info strong { display: block; font-size: 9pt; text-transform: uppercase; color: #333; margin-bottom: 2pt; }
  .gjelder { border: 1px solid #999; border-radius: 4pt; padding: 6pt 8pt; margin-bottom: 12pt; }
  .gjelder strong { font-size: 12pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 5pt 4pt; border-bottom: 1px solid #ccc; text-align: left; vertical-align: top; }
  th { background: #f2f2f2; font-size: 9.5pt; }
  .h { text-align: right; white-space: nowrap; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .summer { margin-top: 10pt; margin-left: auto; width: 60%; }
  .summer div { display: flex; justify-content: space-between; padding: 2pt 0; }
  .total { margin-top: 4pt; border: 2px solid #111; border-radius: 6pt; padding: 8pt 10pt; align-items: baseline; }
  .total .belop { font-size: 18pt; font-weight: bold; }
  footer { margin-top: 18pt; font-size: 9.5pt; color: #333; border-top: 1px solid #999; padding-top: 6pt; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>Prisoverslag</h1>
      <div class="selger">${e(butikk.selskap)} · org.nr. ${e(butikk.orgnr)} · ${e(butikk.adresse)}</div>
    </div>
    <div class="datoboks">${e(dato)}</div>
  </header>

  <div class="info">
    <div><strong>Klubb</strong>${e(klubbNavn)}${sar ? `<br><span class="muted">${e(sar)}</span>` : ''}</div>
    ${til ? `<div><strong>Til</strong>${til}</div>` : ''}
    <div><strong>Kontakt hos oss</strong>${kontaktHosOss ? `${e(kontaktHosOss)}, ` : ''}${e(butikk.navn)}<br>${e(butikk.telefon)}<br>${e(butikk.epost)}</div>
  </div>

  ${innlegg?.tittel ? `<div class="gjelder">Gjelder: <strong>${e(innlegg.tittel)}</strong></div>` : ''}

  <table>
    <thead><tr><th>Vare / arbeid</th><th class="h">Antall</th><th class="h">Pris</th>${harRabatt ? '<th class="h">Rabatt</th>' : ''}<th class="h">Sum</th></tr></thead>
    <tbody>${rader}</tbody>
  </table>

  <div class="summer">
    ${sum.rabattKr > 0 ? `<div><span>Rabatt totalt</span><span>${e(kr(sum.rabattKr))}</span></div>` : ''}
    <div class="total"><span>Sum inkl. mva</span><span class="belop">${e(kr(sum.total))}</span></div>
    <div class="muted"><span>Herav 25 % mva</span><span>${e(kr(mva))}</span></div>
  </div>

  <footer>Dette er et prisoverslag, ikke et bindende tilbud. Prisene gjelder per ${e(dato)} og kan endres ved endret antall, størrelser eller trykk. Alle priser er inkl. 25 % mva. Ta kontakt med oss for å bestille.</footer>
</body>
</html>`;
}

// Åpner utskriftsvinduet (der kan det også lagres som PDF). Gir false hvis nettleseren stoppet det.
export function skrivUtPrisoverslag(valg) {
  const vindu = window.open('', '', 'width=900,height=1100');
  if (!vindu) return false;
  vindu.document.write(prisoverslagHtml(valg));
  vindu.document.close();
  vindu.focus();
  vindu.onafterprint = () => vindu.close();
  setTimeout(() => vindu.print(), 200);
  return true;
}
